import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import GoogleSheetsTable from './GoogleSheetsTable'
import SearchModal from './SearchModal'

interface LawReferenceProps {
  isOpen: boolean
  onClose: () => void
}

const LawReference: React.FC<LawReferenceProps> = ({ isOpen, onClose }) => {
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

  // Блокируем скролл при открытии модального окна
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
      document.documentElement.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
      document.documentElement.style.overflow = 'unset'
    }
  }, [isOpen])

  const sections = [
    { id: 'dk', title: 'ДК', description: 'Дорожный кодекс', color: 'from-blue-600 to-blue-800' },
    { id: 'ak', title: 'АК', description: 'Административный кодекс', color: 'from-green-600 to-green-800' },
    { id: 'uk', title: 'УК', description: 'Уголовный кодекс', color: 'from-red-600 to-red-800' },
    { id: 'additional', title: 'Дополнительно', description: '', color: 'from-purple-600 to-purple-800' }
  ]

  return (
    <AnimatePresence>
      {isOpen && (
                 <motion.div
           initial={{ opacity: 0 }}
           animate={{ opacity: 1 }}
           exit={{ opacity: 0 }}
           className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[9999] backdrop-blur-xl flex items-center justify-center p-4 sm:p-6 lg:p-8"
           style={{ 
             margin: 0, 
             padding: 0, 
             position: 'fixed',
             top: 0,
             left: 0,
             right: 0,
             bottom: 0,
             width: '100vw',
             height: '100vh'
           }}
           onClick={onClose}
         >
                     {/* Кнопка закрытия */}
           <button
             onClick={onClose}
             className="absolute top-2 sm:top-4 right-2 sm:right-4 z-[10000] bg-white/10 hover:bg-white/20 text-white w-8 h-8 sm:w-10 sm:h-10 rounded-lg backdrop-blur-sm transition-all duration-200 flex items-center justify-center text-sm sm:text-base"
           >
            ✕
          </button>

                     {/* Контейнер с плашками */}
           <div 
             className="relative max-w-6xl w-full px-4 sm:px-6 lg:px-8"
             onClick={(e) => e.stopPropagation()}
           >
             {/* Заголовок */}
             <motion.div
               initial={{ opacity: 0, y: -20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ delay: 0.1 }}
               className="text-center mb-6 sm:mb-8 lg:mb-12"
             >
               <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2 sm:mb-3 lg:mb-4">
                 Памятка by HUGO
               </h1>
               <p className="text-gray-400 text-sm sm:text-base lg:text-lg xl:text-xl">
                 Выберите раздел для просмотра
               </p>
             </motion.div>

             {/* Строка поиска */}
             <motion.div
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ delay: 0.2 }}
               className="mb-6 sm:mb-8"
             >
               <div className="relative max-w-2xl mx-auto">
                                   <div className="relative">
                                         <input
                       type="text"
                       placeholder="Поиск по законам и статьям..."
                       value={searchQuery}
                       onChange={(e) => setSearchQuery(e.target.value)}
                                               onKeyPress={(e) => {
                          if (e.key === 'Enter' && searchQuery.trim()) {
                            setIsSearchModalOpen(true)
                          }
                        }}
                       className="w-full px-4 sm:px-6 py-3 sm:py-4 pl-10 sm:pl-14 pr-10 sm:pr-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl sm:rounded-2xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all duration-300 text-sm sm:text-base"
                     />
                    <div className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm sm:text-base">
                      🔍
                    </div>
                                         <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2">
                       <button
                                                   onClick={() => {
                            if (searchQuery.trim()) {
                              setIsSearchModalOpen(true)
                            }
                          }}
                         className="bg-purple-500/20 backdrop-blur-sm px-2 sm:px-3 py-1 rounded-lg text-xs text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-all duration-200 cursor-pointer"
                       >
                         Enter
                       </button>
                     </div>
                  </div>
               </div>
             </motion.div>

                            {/* Сетка плашек */}
               <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
                 {/* Верхний ряд - 3 плашки */}
                 <div className="flex flex-col sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                   {sections.slice(0, 3).map((section) => (
                     <div
                       key={section.id}
                       className="rounded-xl sm:rounded-2xl lg:rounded-3xl cursor-pointer shadow-2xl border border-white/20 backdrop-blur-sm relative overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-3xl group h-20 sm:h-auto sm:aspect-[3/4] flex items-center sm:flex-col sm:justify-center"
                       onClick={() => setSelectedSection(section.id)}
                     >
                       {/* Мобильная версия - горизонтальная карточка */}
                       <div className="flex items-center w-full h-full p-3 sm:hidden">
                         {/* Иконка слева */}
                         <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0 mr-3">
                           {section.id === 'dk' && <span className="text-lg">🚗</span>}
                           {section.id === 'ak' && <span className="text-lg">⚖️</span>}
                           {section.id === 'uk' && <span className="text-lg">⚔️</span>}
                         </div>
                         
                         {/* Текст справа */}
                         <div className="flex-1 min-w-0">
                           <div className="text-base font-bold text-white mb-1 truncate">
                             {section.title}
                           </div>
                           <div className="text-xs text-white/80 truncate">
                             {section.description}
                           </div>
                         </div>
                         
                         {/* Стрелка */}
                         <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 ml-2">
                           <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                           </svg>
                         </div>
                       </div>

                       {/* Десктопная версия - с фоновыми картинками */}
                       <div className="hidden sm:block w-full h-full">
                         {/* Фоновые картинки */}
                         {section.id === 'dk' && (
                           <img 
                             src="/content/Prompt/DK.png" 
                             alt="Дорожный кодекс" 
                             className="absolute inset-0 w-full h-full object-cover blur-sm transition-all duration-500 group-hover:scale-110 group-hover:blur-none"
                           />
                         )}
                         {section.id === 'ak' && (
                           <img 
                             src="/content/Prompt/AK.png" 
                             alt="Административный кодекс" 
                             className="absolute inset-0 w-full h-full object-cover blur-sm transition-all duration-500 group-hover:scale-110 group-hover:blur-none"
                           />
                         )}
                         {section.id === 'uk' && (
                           <img 
                             src="/content/Prompt/UK.png" 
                             alt="Уголовный кодекс" 
                             className="absolute inset-0 w-full h-full object-cover blur-sm transition-all duration-500 group-hover:scale-110 group-hover:blur-none"
                           />
                         )}
                         
                         {/* Затемнение для лучшей читаемости текста */}
                         <div className="absolute inset-0 bg-black/30 transition-all duration-300 group-hover:bg-black/20"></div>
                         
                         {/* Контент поверх картинки */}
                         <div className="relative z-10 p-4 lg:p-6 text-center h-full flex flex-col justify-center">
                           <div className="text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-1 lg:mb-2 drop-shadow-lg transition-all duration-300 group-hover:text-2xl lg:group-hover:text-3xl xl:group-hover:text-4xl">
                             {section.title}
                           </div>
                           <div className="text-sm lg:text-base font-medium text-white/90 drop-shadow-md transition-all duration-300 group-hover:text-white group-hover:text-base lg:group-hover:text-lg">
                             {section.description}
                           </div>
                           
                           {/* Индикатор наведения */}
                           <div className="mt-2 lg:mt-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                             <div className="inline-flex items-center px-3 lg:px-4 py-1.5 lg:py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium border border-white/30">
                               <span>Нажмите для просмотра</span>
                               <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                               </svg>
                             </div>
                           </div>
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>

                 {/* Нижний ряд - 1 плашка */}
                 <div className="flex justify-center">
                   <div
                     className="rounded-xl sm:rounded-2xl lg:rounded-3xl p-2 sm:p-3 text-center cursor-pointer shadow-2xl border border-white/20 backdrop-blur-2xl bg-white/10 relative overflow-hidden w-full max-w-7xl transition-all duration-300 hover:scale-105 hover:shadow-3xl group flex items-center justify-center h-12 sm:h-14 lg:h-16"
                     onClick={() => setSelectedSection(sections[3].id)}
                   >
                     {/* Сильное размытие фона */}
                     <div className="absolute inset-0 backdrop-blur-3xl bg-white/5"></div>
                     
                     {/* Контент поверх размытия */}
                     <div className="relative z-10">
                       <div className="text-base sm:text-lg lg:text-xl font-bold text-white mb-0.5 drop-shadow-lg transition-all duration-300 group-hover:text-lg sm:group-hover:text-xl lg:group-hover:text-2xl">
                         {sections[3].title}
                       </div>
                       <div className="text-white/90 text-xs sm:text-sm font-medium drop-shadow-md transition-all duration-300 group-hover:text-white group-hover:text-sm sm:group-hover:text-base">
                         {sections[3].description}
                       </div>
                     </div>
                   </div>
                 </div>
               </div>

                               {/* Отображение таблицы при выборе секции */}
                <AnimatePresence>
                  {selectedSection && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                                             className="fixed inset-0 z-[10001] backdrop-blur-md bg-black/70 flex items-center justify-center p-4 sm:p-6 lg:p-8"
                      onClick={() => setSelectedSection(null)}
                    >
                                             <div 
                         className="relative max-w-7xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto scrollbar-hide"
                         onClick={(e) => e.stopPropagation()}
                       >
                        {/* Кнопка закрытия таблицы */}
                        <button
                          onClick={() => setSelectedSection(null)}
                          className="absolute top-2 sm:top-4 right-2 sm:right-4 z-[10002] bg-white/10 hover:bg-white/20 text-white w-8 h-8 sm:w-10 sm:h-10 rounded-lg backdrop-blur-sm transition-all duration-200 flex items-center justify-center text-sm sm:text-base"
                        >
                          ✕
                        </button>
                        
                                                 <GoogleSheetsTable 
                           title={`${sections.find(s => s.id === selectedSection)?.title} ${sections.find(s => s.id === selectedSection)?.description}`}
                           customRange={
                             selectedSection === 'ak' ? 'АК.УК.ДК.ПК!C3:E13' :
                             selectedSection === 'uk' ? 'АК.УК.ДК.ПК!G3:L47,АК.УК.ДК.ПК!G49:L54' :
                             selectedSection === 'additional' ? 'АК.УК.ДК.ПК!N2:P11,АК.УК.ДК.ПК!N13:P18,АК.УК.ДК.ПК!N20:P23,АК.УК.ДК.ПК!N25:P30,АК.УК.ДК.ПК!N32:P37,АК.УК.ДК.ПК!N39:T41,АК.УК.ДК.ПК!R4:T8,АК.УК.ДК.ПК!R10:T12,АК.УК.ДК.ПК!R14:T19,АК.УК.ДК.ПК!R21:T27,АК.УК.ДК.ПК!R29:T33,АК.УК.ДК.ПК!R35:T37' :
                             undefined
                           }
                           isAdditional={selectedSection === 'additional'}
                         />
                      </div>
                    </motion.div>
                  )}
                               </AnimatePresence>

             {/* Модальное окно поиска */}
             <SearchModal
               isOpen={isSearchModalOpen}
               onClose={() => setIsSearchModalOpen(false)}
               searchQuery={searchQuery}
             />

             {/* Линии соединения (декоративные) */}



          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default LawReference
